import BodySmall from "../components/atoms/typography/BodySmall";
import StatusBadge from "../components/molecules/StatusBadge";
import type {
  DistributionType,
  LedgerInvoiceOverdueStatus,
  LedgerInvoiceState,
  RuleValidationResult,
} from "../models/InvoiceModels";
import type { InvoiceServiceType } from "../models/InvoiceServiceType";
import type {
  InvoiceAssessmentStatus,
  InvoiceProcessStatus,
  InvoiceValidationStatus,
} from "../models/InvoiceStatusModel";
import type { InvoiceTag } from "../models/InvoiceTags";

const invoiceTagTranslationKeys: Record<InvoiceTag, string> = {
  WIP: "invoices.wip",
  AWAITING_ISSUER_RESPONSE: "invoices.awaitingResponseFromIssuer",
  AWAITING_REGISTRATION: "invoices.awaitingRegistration",
  BUG_REPORTED: "invoices.bugReported",
  AWAITING_INTERNAL_DECISION: "invoices.awaitingInternalDecision",
};

export const getInvoiceTagTitleKey = (tag: InvoiceTag): string =>
  invoiceTagTranslationKeys[tag];

export const getInvoiceListStatusBadge = (
  status: InvoiceProcessStatus,
  small: boolean = true,
) => {
  switch (status) {
    case "REGISTRATION":
      return (
        <StatusBadge
          status="other"
          label="invoices.underRegistration"
          small={small}
        />
      );
    case "VALIDATION":
      return (
        <StatusBadge
          status="pending"
          label="invoices.underValidation"
          small={small}
        />
      );
    case "ASSESSMENT":
      return (
        <StatusBadge
          status="info"
          label="invoices.underAssessment"
          small={small}
        />
      );
    case "REJECTED":
      return (
        <StatusBadge
          status="failed"
          label="documentHandler.rejected"
          small={small}
        />
      );
    case "DONE":
      return (
        <StatusBadge status="checked" label="invoices.done" small={small} />
      );
    default:
      return <StatusBadge status="disabled" label={status} small={small} />;
  }
};

export const getInvoiceLedgerStateBadge = (
  state?: LedgerInvoiceState,
  small?: boolean,
) => {
  switch (state) {
    case "ACTIVATED":
      return (
        <StatusBadge
          status="info"
          label="ledgerInvoice.activated"
          small={small}
        />
      );
    case "APPROVED":
      return (
        <StatusBadge
          status="info"
          label="ledgerInvoice.approved"
          small={small}
        />
      );
    case "CLOSED":
      return (
        <StatusBadge
          status="disabled"
          label="ledgerInvoice.closed"
          small={small}
        />
      );
    case "DONE":
      return (
        <StatusBadge
          status="checked"
          label="ledgerInvoice.done"
          small={small}
        />
      );
    case "NONE":
      return (
        <StatusBadge
          status="disabled"
          label="ledgerInvoice.none"
          small={small}
        />
      );
    case "REGISTRATION":
      return (
        <StatusBadge
          status="other"
          label="ledgerInvoice.registration"
          small={small}
        />
      );
    case "REJECTED":
      return (
        <StatusBadge
          status="failed"
          label="ledgerInvoice.rejected"
          small={small}
        />
      );
    case "UNKNOWN":
      return (
        <StatusBadge
          status="info"
          label="ledgerInvoice.unknown"
          small={small}
        />
      );
    case "RECOURSE":
      return (
        <StatusBadge
          status="failed"
          label="ledgerInvoice.recourse"
          small={small}
        />
      );
    default:
      return (
        <StatusBadge
          status="disabled"
          label={state ?? "ledgerInvoice.unknown"}
          small={small}
        />
      );
  }
};

export const getInvoiceOverdueStatusBadge = (
  overdueStatus?: LedgerInvoiceOverdueStatus,
  small?: boolean,
) => {
  switch (overdueStatus) {
    case "ENFORCEMENT_WARNING":
      return (
        <StatusBadge
          status="pending"
          label="ledgerInvoice.enforcementWarning"
          small={small}
        />
      );
    case "DEBT_COLLECTION":
      return (
        <StatusBadge
          status="failed"
          label="ledgerInvoice.debtCollection"
          small={small}
        />
      );
    case "NONE":
      return <BodySmall>-</BodySmall>;
    case "OVERDUE":
      return (
        <StatusBadge
          status="failed"
          label="ledgerInvoice.overdue"
          small={small}
        />
      );
    case "REMINDER":
      return (
        <StatusBadge
          status="pending"
          label="ledgerInvoice.reminder"
          small={small}
        />
      );
    case "UNKNOWN":
      return <BodySmall>-</BodySmall>;
    default:
      return <BodySmall>{overdueStatus ?? "random.unknown"}</BodySmall>;
  }
};

export const getInvoiceValidationStatusBadge = (
  validationStatus?: InvoiceValidationStatus,
  small?: boolean,
) => {
  switch (validationStatus) {
    case "VALIDATED":
      return (
        <StatusBadge
          status="checked"
          label="invoiceValidation.validationApproved"
          small={small}
        />
      );
    case "VALIDATED_AWAITING_INPUT":
      return (
        <StatusBadge
          status="pending"
          label="invoiceValidation.validationAwaitingInput"
          small={small}
        />
      );
    case "VALIDATED_WITH_WARNINGS":
      return (
        <StatusBadge
          status="pending"
          label="invoiceValidation.validatedWithWarnings"
          small={small}
        />
      );
    case "VALIDATED_MANUALLY":
      return (
        <StatusBadge
          status="checked"
          label="invoiceValidation.manuallyValidated"
          small={small}
        />
      );
    case "VALIDATION_FAILED":
      return (
        <StatusBadge
          status="failed"
          label="invoiceValidation.validationFailed"
          small={small}
        />
      );
    case "VALIDATING":
      return (
        <StatusBadge
          status="disabled"
          label="invoiceValidation.validating"
          small={small}
        />
      );
    case "VALIDATION_ERROR":
      return (
        <StatusBadge
          status="failed"
          label="invoiceValidation.validationError"
          small={small}
        />
      );
    case "UNKNOWN":
      return <StatusBadge status="info" label="random.unknown" small={small} />;
    default:
      return (
        <StatusBadge
          status="disabled"
          label={validationStatus ?? "random.unknown"}
          small={small}
        />
      );
  }
};

export const getInvoiceAssessmentStatusBadge = (
  assessmentStatus?: InvoiceAssessmentStatus,
) => {
  switch (assessmentStatus) {
    case "APPROVED":
      return (
        <StatusBadge status="checked" label="invoiceAssessment.approved" />
      );
    case "PASSED_AWAITING_INPUT":
      return (
        <StatusBadge status="checked" label="invoiceAssessment.approved" />
      );
    case "ASSESSING":
      return (
        <StatusBadge status="pending" label="invoiceAssessment.assessing" />
      );
    case "ASSESSMENT_ERROR":
      return (
        <StatusBadge
          status="failed"
          label="invoiceAssessment.assessmentError"
        />
      );
    case "ASSESSMENT_FAILED":
      return (
        <StatusBadge
          status="failed"
          label="invoiceAssessment.assessmentFailed"
        />
      );
    case "AWAITING_LG":
      return (
        <StatusBadge
          status="pending"
          label="invoiceAssessment.awaitingLgApproval"
        />
      );
    case "FORCE_APPROVED":
      return (
        <StatusBadge
          status="checked"
          label="invoiceAssessment.forcedApproved"
        />
      );
    case "NONE":
      return <StatusBadge status="disabled" label="invoices.none" />;
    case "UNKNOWN":
      return <StatusBadge status="info" label="random.unknown" />;
    case "SKIPPED":
      return (
        <StatusBadge status="disabled" label="invoiceAssessment.skipped" />
      );
    default:
      return (
        <StatusBadge
          status="disabled"
          label={assessmentStatus ?? "random.unknown"}
        />
      );
  }
};

export const getInvoiceServiceTypeTranslationKey = (
  serviceType: InvoiceServiceType,
): string => {
  switch (serviceType) {
    case "ADMINISTER_INVOICE":
      return "documentHandler.adminOnly";
    case "NONE":
      return "-";
    case "SELL_INVOICE":
      return "documentHandler.sellInvoice";
    case "UNKNOWN":
      return "random.unknown";
    default:
      return serviceType;
  }
};
export const getInvoiceDistributionTypeTranslationKey = (
  distributionType?: DistributionType,
): string => {
  switch (distributionType) {
    case "EINVOICE":
      return "invoices.eInvoice";
    case "EMAIL":
      return "common.email";
    case "PDF":
      return "PDF";
    case "UNKNOWN":
      return "random.unknown";
    case "PRINT":
      return "invoices.print";
    default:
      return distributionType ?? "random.unknown";
  }
};

export const getInvoiceDistrubitionTypeRecipientAddress = (
  distributionType?: DistributionType,
  ediAddress?: string | null,
  recipientEmail?: string | null,
  address?: {
    street?: string;
    city?: string;
    postalCode?: string;
    country?: string;
  } | null,
): string => {
  switch (distributionType) {
    case "EINVOICE":
      return ediAddress ?? "Ingen EDI-adress angiven";
    case "EMAIL":
      return recipientEmail ?? "Ingen epostadress angiven";
    case "PDF":
      return "Ingen aning om vad PDF-distrubution ska va";
    case "PRINT":
      if (address) {
        const { street, city, postalCode, country } = address;
        return `${street ?? ""}, ${postalCode ?? ""} ${city ?? ""}, ${
          country ?? ""
        }`;
      }
      return "Ingen adress angiven";
    default:
      return "random.unknown";
  }
};

export const getInvoiceValidationRuleBadge = (
  ruleResult?: RuleValidationResult,
  small?: boolean,
) => {
  switch (ruleResult) {
    case "PASSED":
      return (
        <StatusBadge
          status="checked"
          label="invoiceValidation.rulePassed"
          small={small}
        />
      );
    case "PASSED_WITH_WARNINGS":
      return (
        <StatusBadge
          status="info"
          label="invoiceValidation.rulePassedWithWarnings"
          small={small}
        />
      );
    case "FAILED":
      return (
        <StatusBadge
          status="failed"
          label="invoiceValidation.ruleFailed"
          small={small}
        />
      );
    case "ERROR":
      return (
        <StatusBadge
          status="failed"
          label="invoiceValidation.ruleError"
          small={small}
        />
      );
    case "UNKNOWN":
      return <StatusBadge status="info" label="random.unknown" small={small} />;
    default:
      return (
        <StatusBadge
          status="disabled"
          label={ruleResult ?? "random.unknown"}
          small={small}
        />
      );
  }
};
